import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import EpisodeList from "@/pages/detail/components/EpisodeList";
import RecommendList from "@/pages/detail/components/RecommendList";
import { mediaList } from "@/mocks/media";
import { useFavorites } from "@/hooks/useFavorites";
import type { MediaItem } from "@/types/media";
import { findMediaById, formatViews, getRecommendations, getTypeLabel } from "@/utils/media";

export default function DetailPage() {
  const { id } = useParams();
  const all = mediaList as MediaItem[];
  const item = findMediaById(all, id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  if (!item) {
    return (
      <PageLayout title="影片不存在 - 桔子TV" description="未找到对应的影视内容，可能已下架或链接有误。">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-24 text-center">
          <span className="w-20 h-20 mx-auto rounded-full bg-background-200 flex items-center justify-center">
            <i className="ri-emotion-sad-line text-4xl text-foreground-500" />
          </span>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 mt-5">影片不存在或已下架</h1>
          <p className="text-sm text-foreground-600 mt-2.5">该内容可能已被移除，去看看其他热门影视吧</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/" className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer">
              <i className="ri-home-4-line" />
              返回首页
            </Link>
            <Link to="/category" className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-background-100 border border-background-300 hover:border-primary-400 text-foreground-950 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer">
              <i className="ri-compass-3-line" />
              浏览分类
            </Link>
          </div>
        </div>
      </PageLayout>
    );
  }

  const favorite = isFavorite(item.id);
  const recommendations = getRecommendations(all, item, 6);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}${__BASE_PATH__}/detail/${item.id}`;
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        showToast("链接已复制到剪贴板");
      } else {
        showToast("请手动复制浏览器地址栏链接");
      }
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
  };

  const metaRows = [
    { label: "导演", value: item.director },
    { label: "主演", value: item.actors.join("  /  ") },
    { label: "类型", value: item.genres.join(" / ") },
    { label: "地区", value: item.region },
    { label: "年份", value: String(item.year) },
    { label: "状态", value: item.updateStatus },
  ];

  return (
    <PageLayout
      title={`${item.title}在线观看完整版 - 桔子TV`}
      description={item.description.slice(0, 78)}
    >
      <div className="relative w-full h-[240px] md:h-[320px] overflow-hidden">
        <img
          src={item.banner || item.cover}
          alt={`${item.title} 剧照`}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/70 to-background-50/40" />
      </div>

      <div className="max-w-[1280px] mx-auto px-4 md:px-8 -mt-24 md:-mt-28 relative z-10">
        <nav className="flex items-center gap-1.5 text-xs md:text-sm text-foreground-600 mb-4" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <Link to={`/category/${item.type}`} className="hover:text-primary-500 transition-colors cursor-pointer">{getTypeLabel(item.type)}</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-950 line-clamp-1">{item.title}</span>
        </nav>

        <div className="rounded-lg bg-background-100 border border-background-200 p-4 md:p-6">
          <div className="flex flex-col md:flex-row gap-5 md:gap-7">
            <div className="relative w-full md:w-[210px] shrink-0 rounded-lg overflow-hidden">
              <div className="relative w-full aspect-[2/3]">
                <img
                  src={item.cover}
                  alt={`${item.title} 主海报`}
                  title={`${item.title} 在线观看`}
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
              </div>
              <Link
                to={`/play/${item.id}/1`}
                className="absolute inset-0 flex items-center justify-center bg-background-950/40 opacity-0 hover:opacity-100 transition-opacity cursor-pointer"
              >
                <span className="w-14 h-14 rounded-full bg-primary-500 text-background-50 flex items-center justify-center">
                  <i className="ri-play-fill text-3xl" />
                </span>
              </Link>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950 leading-tight">{item.title}</h1>

              <div className="flex flex-wrap items-center gap-2 mt-3">
                <span className="px-2 py-0.5 rounded bg-primary-500 text-background-50 text-xs font-medium font-label">{getTypeLabel(item.type)}</span>
                {item.tags.map((tag) => (
                  <span key={tag} className="px-2 py-0.5 rounded bg-secondary-500/15 text-secondary-300 text-xs font-medium font-label">{tag}</span>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-4 text-xs md:text-sm text-foreground-600">
                <span className="flex items-center gap-1.5">
                  <i className="ri-star-fill text-accent-500" />
                  <strong className="text-foreground-950 font-semibold text-base">{item.rating.toFixed(1)}</strong>
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-fire-line text-primary-500" />
                  {formatViews(item.views)} 次播放
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-play-list-2-line" />
                  共 {item.episodeCount} 集 · {item.updateStatus}
                </span>
              </div>

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5 mt-5">
                {metaRows.map((row) => (
                  <div key={row.label} className="flex gap-2 text-sm">
                    <dt className="text-foreground-600 shrink-0 w-10">{row.label}</dt>
                    <dd className="text-foreground-800 line-clamp-1">{row.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="flex flex-wrap items-center gap-3 mt-6">
                <Link
                  to={`/play/${item.id}/1`}
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer"
                >
                  <i className="ri-play-fill text-lg" />
                  立即播放
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    const nowFavorite = toggleFavorite(item.id);
                    showToast(nowFavorite ? "已加入收藏" : "已取消收藏");
                  }}
                  className={`inline-flex items-center gap-2 h-11 px-5 rounded-md border text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    favorite
                      ? "bg-accent-500/15 border-accent-500 text-accent-400"
                      : "bg-background-200/60 border-background-300 text-foreground-800 hover:border-accent-400 hover:text-accent-400"
                  }`}
                >
                  <i className={favorite ? "ri-heart-3-fill" : "ri-heart-3-line"} />
                  {favorite ? "已收藏" : "收藏"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-background-200/60 border border-background-300 hover:border-primary-400 text-foreground-800 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
                >
                  <i className="ri-share-forward-line" />
                  分享
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mt-8">
          <div className="lg:col-span-2 space-y-6">
            <section className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-primary-500" />
                剧情简介
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-loose">{item.description}</p>
            </section>

            <section className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-primary-500" />
                剧集目录
                <span className="text-xs text-foreground-600 font-normal">（点击集数进入播放，演示）</span>
              </h2>
              <EpisodeList item={item} />
            </section>
          </div>

          <aside className="space-y-6">
            <section className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500" />
                相关推荐
              </h2>
              <RecommendList items={recommendations} />
            </section>
          </aside>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-lg bg-background-100 border border-primary-500/50 text-sm text-foreground-950 animate-fade-in">
          {toast}
        </div>
      )}
    </PageLayout>
  );
}